import {IconsService} from '$services/icons/icons.service'
import {HorseCategory, PostDisplay, PostFilters, PostStatus, PostWithDatasDTO, User} from '$types'
import {
  AfterViewChecked, AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core'
import {FormControl} from '@angular/forms'
import {MatSelect, MatSelectChange} from '@angular/material/select'
import {TranslateService} from '@ngx-translate/core'
import {mergeMap, Observable, Subscription} from 'rxjs'
import {AuthService} from 'src/app/services/auth/auth.service'
import {PostsService} from "$services/posts/posts.service";
import {ActivatedRoute, Router} from "@angular/router";
import {MatInput} from "@angular/material/input";
import {EventBusService} from "$services/auth/event/event-bus.service";

interface IFilters {
  name: string | null
  status: 'any' | 'published' | 'waiting' | 'archived'
  category: 'any' | 'yearling' | 'training' | 'two_years' | 'out_training' | 'broodmare' | 'stallion'
  author: 'any' | 'me' | string
}

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.css'],
})
export class FiltersComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy, AfterViewInit {
  @Input() posts: PostDisplay[] = []
  @Output() postsChange = new EventEmitter<PostDisplay[]>()
  @Output() postLayout = new EventEmitter<boolean>();

  //filters
  orderBy!: string;
  @ViewChild('statusSelect') statusSelect!: MatSelect;
  @ViewChild('sortSelect') sortSelect!: MatSelect;
  @ViewChild('categorySelect') categorySelect!: MatSelect;
  @ViewChild('nameInput') nameInput!: ElementRef;
  @ViewChild('authorInput') authorInput!: ElementRef;
  @ViewChild('authorInput') authorMatInput!: MatInput;

  filterByAuthorControl = new FormControl('any')
  filteredAuthors!: Observable<string | null>
  filteredPosts: PostDisplay[] = []
  user?: User | null
  is_loading: boolean = true
  orderSubscription = new Subscription();
  routeSubscription = new Subscription();
  prev: string = "";
  timer: any;


  filters: IFilters = {
    name: null,
    status: 'any',
    category: 'any',
    author: 'any',
  }
  currentPostsLayout: boolean // posts layout : true = list, false = grid

  constructor(
    private readonly authService: AuthService,
    private readonly translateService: TranslateService,
    private readonly icons: IconsService,
    private readonly postService: PostsService,
    private readonly eventBusService: EventBusService,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.icons.registerIcon([`layout-grid`, `layout-list`,])
    this.user = this.authService.user
    this.currentPostsLayout = true
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges()
  }

  get filtered_author(): string | null {
    const value: string | null = this.filterByAuthorControl.value
    if (value === 'any' || value === 'me') {
      return this.translateService.instant(
        `posts.filters.author.options.${value}`
      )
    }
    return value
  }

  ngOnInit(): void {
    this.filteredPosts = this.posts
    this.filteredAuthors = this.filterByAuthorControl.valueChanges.pipe(
      mergeMap((value) => {
        this.submitFilter();
        return [value].filter((v) => !v!.match(/^(me|any)$/g))
      })
    );
    this.postLayout.emit(this.currentPostsLayout);
    this.orderSubscription = this.postService.orderPost.subscribe(value => {
      this.orderBy = value;
      this.submitFilter();
    });
    this.eventBusService.on(
      'login', (action: any) => {
        this.categorySelect.value = "";
        this.statusSelect.value = "";
        this.nameInput.nativeElement.value = "";
      }
    );

  }

  isUndefinedOrEmpty(value: string) {
    return !value || value.trim().length === 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.changeDetectorRef.detectChanges()
    if (this.is_loading && changes['posts'].currentValue.length > 0) {
      this.filteredPosts = changes['posts'].currentValue
      this.is_loading = false
    }
  }

  filterByName(): void {
    let horseName = this.nameInput.nativeElement.value;
    if (horseName.length >= 3 || horseName.length === 0) {
      if (horseName !== this.prev) {
        clearTimeout(this.timer);
        this.timer = setTimeout(this.addQueryParams, 750);
        this.prev = horseName;
      }

    }
  }

  submitFilter(): void {
    this.addQueryParams();
  }

  addQueryParams = () => {
      const currentRoute = this.route.snapshot;
      let currentQueryParams = {...currentRoute.queryParams};
      this.erase(currentQueryParams);
      let author = currentQueryParams["author"];
      if (this.isUndefinedOrEmpty(author)) {
          author = this.filterByAuthorControl.value == "Moi" ? "me" : this.filterByAuthorControl.value;
      }
      author = author === "Tous" ? "all" : author;
      author = author === "any" ? "all" : author;
      currentQueryParams = this.validateParam(currentQueryParams, "order-by", this.orderBy);
      currentQueryParams = this.validateParam(currentQueryParams, "horse-name", this.nameInput.nativeElement.value);
      currentQueryParams = this.validateParam(currentQueryParams, "status", this.statusSelect.value);
      currentQueryParams = this.validateParam(currentQueryParams, "category", this.categorySelect.value);
      currentQueryParams = this.validateParam(currentQueryParams, "author", author);
      this.router.navigate([], {
          relativeTo: this.route,
          queryParams: currentQueryParams,
          queryParamsHandling: 'merge',
      });
    };

  validateParam(currentQueryParams: any, paramName: string, value: string) {
    if (!value || value.trim().length === 0) {
      currentQueryParams[paramName] = null;
    }

    if (value && value.trim().length !== 0) {
      currentQueryParams[paramName] = value;
    }
    return currentQueryParams;
  }

  erase(currentQueryParams: any) {
    currentQueryParams["author"] = null;
    currentQueryParams["horse-name"] = null;
    currentQueryParams["category"] = null;
    currentQueryParams["status"] = null;
    currentQueryParams["order-by"] = null;
  }

  filterByCategory(event: MatSelectChange): void {
    this.filters.category = event.value
    this.applyFilters()
  }

  filterByAuthor(value: string | null): void {
    if (value == null) {
      return
    }
    if (!value.match(/^(me|any)$/g)) {
      return
    }
    this.filters.author = value
    this.applyFilters()
  }

  applyFilters(): void {
    this.posts = this.filteredPosts.filter((post: PostWithDatasDTO) => {
//       const hasSameName =
//         post.horse.name.length === 0 ||
//         post.horse.name
//           .toLowerCase()
//           .includes(this.filters.name?.toLowerCase() || '')
      const horseName = (post.horse?.name?.toLowerCase() || '').replace(/\s/g, '');
      const filtersName = (this.filters?.name?.toLowerCase() || '').replace(/\s/g, '');
      const hasSameName = horseName.length === 0 || horseName.includes(filtersName);

      const hasStatus = this.isPostHasStatus(post)

      const hasCategory = this.isPostHasCategory(post)

      const isAuthor =
        this.filters.author.toLowerCase() === 'any' ||
        (this.filters.author.toLowerCase() === 'me' &&
          post.idClient === this.user?.id)
      return hasSameName && isAuthor && hasStatus && hasCategory
    })
    this.postsChange.emit(this.posts)
  }

  private isPostHasStatus(post: PostWithDatasDTO): boolean {
    const statusLowerCase = this.filters.status.toLowerCase()
    const hasStatus =
      (statusLowerCase === 'any') ||
      this.statusPublished(statusLowerCase, post) ||
      this.statusWaitingValidation(statusLowerCase, post) ||
      this.statusWaitingPayment(statusLowerCase, post) ||
      this.statusSaved(statusLowerCase, post) ||
      this.statusExpired(statusLowerCase, post) ||
      this.statusArchived(statusLowerCase, post)
    return hasStatus
  }

  private isPostHasCategory(post: PostWithDatasDTO): boolean {
    const categoryLowerCase = this.filters.category.toLowerCase()
    const hasCategory =
      (categoryLowerCase === 'any') ||
      categoryLowerCase === 'yearling' && post.category === HorseCategory.YEARLING ||
      categoryLowerCase === 'training' && post.category === HorseCategory.TRAINING ||
      categoryLowerCase === 'two_years' && post.category === HorseCategory.TWO_YEARS ||
      categoryLowerCase === 'out_training' && post.category === HorseCategory.OUT_TRAINING ||
      categoryLowerCase === 'broodmare' && post.category === HorseCategory.BROODMARE ||
      categoryLowerCase === 'stallion' && post.category === HorseCategory.STALLION
    return hasCategory
  }

  private statusArchived(statusLowerCase: string, post: PostWithDatasDTO): boolean {
    return statusLowerCase === 'archived' && post.status === PostStatus.ARCHIVED
  }

  private statusExpired(statusLowerCase: string, post: PostWithDatasDTO): boolean {
    return statusLowerCase === 'expired' && post.status === PostStatus.EXPIRED
  }

  private statusSaved(statusLowerCase: string, post: PostWithDatasDTO): boolean {
    return statusLowerCase === 'saved' && post.status === PostStatus.DRAFT
  }

  private statusWaitingPayment(statusLowerCase: string, post: PostWithDatasDTO): boolean {
    return statusLowerCase === 'waiting_payment' && post.status === PostStatus.WAITING_PAYMENT
  }

  private statusWaitingValidation(statusLowerCase: string, post: PostWithDatasDTO): boolean {
    return statusLowerCase === 'waiting_validation' && post.status === PostStatus.WAITING_VALIDATION
  }

  private statusPublished(statusLowerCase: string, post: PostWithDatasDTO): boolean {
    return statusLowerCase === 'published' && post.status === PostStatus.PUBLISHED
  }

  togglePostsLayout(): void {
    this.currentPostsLayout = !this.currentPostsLayout
    this.postLayout.emit(this.currentPostsLayout)
  }

  ngOnDestroy(): void {
    this.orderSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      let horseName = params["horse-name"];
      let orderBy = params["order-by"];
      let status = params["status"];
      let category = params["category"];
      let author = params["author"];
      if (this.isUndefinedOrEmpty(horseName)
        && this.isUndefinedOrEmpty(orderBy)
        && this.isUndefinedOrEmpty(status)
        && this.isUndefinedOrEmpty(category)
        && this.isUndefinedOrEmpty(author)) {
        return;
      }
      const postFilters: PostFilters = {
        horseName: horseName,
        status: status,
        orderBy: orderBy,
        category: category,
        author: author
      };
      this.nameInput.nativeElement.value = !horseName ? "" : horseName;
      this.statusSelect.value = !status ? "" : status;
      this.categorySelect.value = !category ? "" : category;
      author = this.isUndefinedOrEmpty(author) ? "Tous" : author;
      author = author === "me" ? "Moi" : author;
      author = author === "all" ? "Tous" : author;
      this.filterByAuthorControl.setValue(author);
      this.postService.emitOrderPost(!orderBy ? "" : orderBy);
      this.postService.getPostsWithFilters(postFilters).subscribe({});
    });
  }
}
